import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Dropdown, Modal, Table, Tag } from "antd";
import { IconArrowRight, IconPlus } from "@tabler/icons-react";
import CustomBtn from "../../components/btn/CustomBtn";
import CustomInput from "../../components/input/CustomInput";
import CustomTextarea from "../../components/input/CustomTextarea";
import PageHeader from "../../components/header/PageHeader";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { useToast } from "../../components/toast";
import api from "../../api";

const MODULE_LABELS = {
  dashboard: "Дашбоард",
  user: "Хэрэглэгч",
  staff: "Ажилтан",
  class: "Өрөө",
  schedule: "Хуваарь",
  bundle: "Багц",
  banner: "Баннер",
  upload: "Зураг оруулах",
  role: "Эрх",
};

const ACTION_LABELS = {
  view: "Харах",
  create: "Үүсгэх",
  update: "Засах",
  delete: "Устгах",
  deactivate: "Идэвхгүй болгох",
  assign: "Олгох",
  extend: "Сунгах",
  cancel: "Цуцлах",
  view_user: "Хэрэглэгчийн багц харах",
  manage: "Удирдах",
};

const KEY_RE = /^[a-z][a-z0-9_]{1,49}$/;
const emptyForm = { key: "", name: "", description: "", permissions: [] };

export default function Role() {
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]); // [{ module, items: [{ key, action }] }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const toast = useToast();

  const totalPermissions = useMemo(
    () => groups.reduce((sum, g) => sum + g.items.length, 0),
    [groups],
  );

  const fetchRoles = async () => {
    setLoading(true);
    const res = await api.get_roles();
    if (res?.status === 200) setRoles(res.data.data);
    setLoading(false);
  };

  const fetchPermissions = async () => {
    const res = await api.get_permissions();
    if (res?.status === 200) setGroups(res.data.data);
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const openCreate = () => {
    setSelected(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (role) => {
    setSelected(role);
    setForm({
      key: role.key,
      name: role.name,
      description: role.description || "",
      permissions: role.permissions,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setForm(emptyForm);
  };

  const togglePermission = (key, checked) =>
    setForm((f) => ({
      ...f,
      permissions: checked
        ? [...f.permissions, key]
        : f.permissions.filter((k) => k !== key),
    }));

  const toggleModule = (items, checked) => {
    const keys = items.map((i) => i.key);
    setForm((f) => ({
      ...f,
      permissions: checked
        ? Array.from(new Set([...f.permissions, ...keys]))
        : f.permissions.filter((k) => !keys.includes(k)),
    }));
  };

  const isValid =
    form.name.trim() &&
    form.permissions.length > 0 &&
    (selected || KEY_RE.test(form.key));

  const submitHandler = async () => {
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      permissions: form.permissions,
    };
    const res = selected
      ? await api.update_role(selected.id, payload)
      : await api.create_role({ ...payload, key: form.key });
    setSaving(false);

    if (res?.status === 200 || res?.status === 201) {
      closeModal();
      fetchRoles();
      toast("Амжилттай", "success");
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
  };

  const deleteHandler = async () => {
    if (!selected) return;
    const res = await api.delete_role(selected.id);
    if (res?.status === 200) {
      fetchRoles();
      toast("Устгалаа", "success");
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
    setSelected(null);
  };

  const columns = [
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      render: (name, r) => (
        <span className="d-flex align-items-center gap-2">
          {name}
          {r.is_system && <Tag color="gold">Систем</Tag>}
        </span>
      ),
    },
    { title: "Түлхүүр", dataIndex: "key", key: "key" },
    {
      title: "Ажилчид",
      dataIndex: "user_count",
      key: "user_count",
      width: 110,
    },
    {
      title: "Эрхүүд",
      key: "permissions",
      width: 120,
      render: (_, r) => `${r.permissions.length} / ${totalPermissions}`,
    },
    {
      title: "Үйлдэл",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Dropdown
          disabled={record.is_system}
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ color: "red", fontSize: 13 }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete") {
                setSelected(record);
                setConfirmVisible(true);
              }
            },
          }}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл
            <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Эрх"
        right={
          <CustomBtn
            title="Эрх нэмэх"
            icon={<IconPlus size={18} />}
            onClick={openCreate}
          />
        }
      />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={roles}
        loading={loading}
        rowHoverable={false}
        pagination={false}
      />

      <Modal
        open={showModal}
        width={720}
        closable={false}
        title={selected ? "Эрх засах" : "Эрх нэмэх"}
        footer={
          <div className="d-flex gap-2 justify-content-end">
            <CustomBtn type="secondary" title="Болих" onClick={closeModal} />
            <CustomBtn
              title="Хадгалах"
              loading={saving}
              disabled={!isValid}
              onClick={submitHandler}
            />
          </div>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div style={{ flex: 1 }}>
              <CustomInput
                required
                title="Нэр"
                placeholder="Ресепшн"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <CustomInput
                required
                title="Түлхүүр"
                placeholder="reception"
                value={form.key}
                disabled={!!selected}
                onChange={(e) =>
                  setForm({ ...form, key: e.target.value.toLowerCase() })
                }
              />
            </div>
          </div>

          <CustomTextarea
            title="Тайлбар"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Эрхүүд ({form.permissions.length} / {totalPermissions})
            </div>
            <div
              className="d-flex flex-column gap-2"
              style={{ maxHeight: 360, overflowY: "auto" }}
            >
              {groups.map((g) => {
                const keys = g.items.map((i) => i.key);
                const checkedCount = keys.filter((k) =>
                  form.permissions.includes(k),
                ).length;

                return (
                  <div
                    key={g.module}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <Checkbox
                      checked={checkedCount === keys.length}
                      indeterminate={
                        checkedCount > 0 && checkedCount < keys.length
                      }
                      onChange={(e) => toggleModule(g.items, e.target.checked)}
                    >
                      <b>{MODULE_LABELS[g.module] || g.module}</b>
                    </Checkbox>

                    <div className="d-flex flex-wrap gap-3 mt-2 ps-4">
                      {g.items.map((p) => (
                        <Checkbox
                          key={p.key}
                          checked={form.permissions.includes(p.key)}
                          onChange={(e) =>
                            togglePermission(p.key, e.target.checked)
                          }
                        >
                          {ACTION_LABELS[p.action] || p.action}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        variant="error"
        title="Устгах уу?"
        description={
          <>
            '<b>{selected?.name}</b>' эрхийг устгах гэж байна. Энэ үйлдлийг
            буцаах боломжгүй.
          </>
        }
        showModal={confirmVisible}
        setShowModal={setConfirmVisible}
        submitHandler={deleteHandler}
        cancelHandler={() => setSelected(null)}
      />
    </>
  );
}
